import React, { useState, useContext, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  TrendingUp,
  Activity,
  DollarSign,
  ShoppingCart,
  Bell,
  User,
  Sparkles,
  ArrowUpRight,
  ArrowDownRight,
  CreditCard,
  Zap,
  Heart,
  Shield,
  ChevronRight,
  Link2,
  AlertCircle,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { MainStore } from "../../stores/Main";
import API from "../../utils/api";
import axios from "axios";
import useModal from "../../hooks/use-modal";
import history from "../../utils/history";
import ApexChart from "react-apexcharts";

const Dashboard = () => {
  const mainStore = useContext(MainStore);
  const [notifications, setNotifications] = useState([]);
  const [scoreData, setScoreData] = useState({});
  const [financialConnected, setFinancialConnected] = useState(false);
  const [emotionalConnected, setEmotionalConnected] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [spendingData, setSpendingData] = useState([]);
  const [alerts, setAlerts] = useState(null);
  const [alert, setAlert] = useState(null);
  const { openModal, modalTypes } = useModal();

  // Animation variants
  const containerAnimation = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemAnimation = {
    hidden: { y: 20, opacity: 0 },
    show: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
      },
    },
  };

  const cardHoverAnimation = {
    scale: 1.02,
    transition: { duration: 0.2 },
  };

  // Fetch data
  const fetchProfile = React.useCallback(() => {
    axios
      .all([
        API.get("/notifications"),
        API.get("/connections"),
        API.get("/scores"),
      ])
      .then(
        axios.spread((notificationR, connections, scores) => {
          if (notificationR.data.notification) {
            setNotifications(notificationR.data.notification.slice(0, 5));
          } else {
            setNotifications([]);
          }

          let emot_score = [];
          let fin_score = [];
          let freede_score = [];

          if (scores.data.score && scores.data.score.length > 0) {
            scores.data.score.forEach((score) => {
              if (score.emotional !== undefined) {
                emot_score.push([score.date.seconds * 1000, score.emotional]);
              }
              if (score.financial !== undefined) {
                fin_score.push([score.date.seconds * 1000, score.financial]);
              }
              if (score.freede !== undefined) {
                freede_score.push([score.date.seconds * 1000, score.freede]);
              }
            });
          }

          const chartOptions = {
            series: [
              { name: "Sentiment", data: emot_score },
              { name: "Financial", data: fin_score },
              { name: "Freede", data: freede_score },
            ],
            options: {
              chart: {
                type: "area",
                height: 350,
                zoom: { enabled: false },
                toolbar: { show: false },
                background: "transparent",
              },
              dataLabels: { enabled: false },
              stroke: {
                curve: "smooth",
                width: 2,
              },
              fill: {
                type: "gradient",
                gradient: {
                  shadeIntensity: 1,
                  opacityFrom: 0.4,
                  opacityTo: 0.1,
                  stops: [0, 90, 100],
                },
              },
              colors: ["#8b5cf6", "#3b82f6", "#10b981"],
              xaxis: {
                type: "datetime",
                labels: {
                  style: { colors: "#94a3b8" },
                },
              },
              yaxis: {
                labels: {
                  style: { colors: "#94a3b8" },
                },
              },
              legend: {
                labels: { colors: "#e2e8f0" },
              },
              grid: {
                borderColor: "#334155",
                strokeDashArray: 4,
              },
              tooltip: {
                theme: "dark",
              },
            },
          };

          setScoreData(chartOptions);
          setLoaded(true);

          // Set spending data
          if (mainStore.profile.top_spending) {
            setSpendingData(mainStore.profile.top_spending);
          }

          if (connections.data.provider) {
            connections.data.provider.forEach((provider) => {
              if (provider.provider === "plaid") {
                setFinancialConnected(true);
              } else {
                setEmotionalConnected(true);
              }
            });
          }
        })
      );
  }, [mainStore.profile.top_spending]);

  const getAlerts = async () => {
    try {
      const response = await API.get("/alerts");
      setAlerts(response.data.alert);
      if (response.data.alert.length > 0) {
        for (let al in response.data.alert) {
          if (
            response.data.alert[al].agency_id !== undefined &&
            mainStore.agency
          ) {
            setAlert(response.data.alert[al]);
            break;
          } else if (
            !mainStore.agency &&
            response.data.alert[al].agency_id === undefined
          ) {
            setAlert(response.data.alert[al]);
          }
        }
      }
    } catch (e) {}
  };

  useEffect(() => {
    fetchProfile();
    getAlerts();
  }, []);

  // Alert modal handling
  useEffect(() => {
    if (alert === null) return;
    switch (alert.type) {
      case "repayment_offer":
        openModal(modalTypes.ModalAlertOffer, {
          notToday: getAlerts,
          startPlan: (config) => {
            openModal(modalTypes.ModalMakePayment, {
              account: { id: config.account },
              amount: config.plan.overtime.initial,
              selectedPlan: config.plan.config,
              mode: "payment-plan",
            });
          },
          alert,
        });
        break;
      case "setup_payment":
        openModal(modalTypes.ModalAlertSetupPayment, {
          makePayment: () => openModal(modalTypes.ModalMakePayment),
          notToday: getAlerts,
          alert,
        });
        break;
      default:
        break;
    }
  }, [alert]);

  // Stat Card Component
  const StatCard = ({ icon: Icon, label, value, change, color, bgColor }) => (
    <motion.div
      variants={itemAnimation}
      whileHover={{ y: -2 }}
      className="relative group"
    >
      <div
        className={`absolute inset-0 bg-gradient-to-r ${color} opacity-0 group-hover:opacity-10 transition-opacity duration-300 rounded-2xl blur-xl`}
      />
      <div className="relative bg-white/[0.03] backdrop-blur-md border border-white/10 rounded-2xl p-6 hover:bg-white/[0.05] transition-all duration-300">
        <div className="flex items-start justify-between mb-4">
          <motion.div
            className={`p-3 rounded-xl bg-gradient-to-br ${bgColor}`}
            whileHover={{ scale: 1.05, rotate: 5 }}
            transition={{ type: "spring", stiffness: 400 }}
          >
            <Icon className="w-6 h-6 text-white" />
          </motion.div>
          {change && (
            <div
              className={`flex items-center gap-1 text-sm ${change > 0 ? "text-green-400" : "text-red-400"}`}
            >
              {change > 0 ? (
                <ArrowUpRight className="w-4 h-4" />
              ) : (
                <ArrowDownRight className="w-4 h-4" />
              )}
              {Math.abs(change)}%
            </div>
          )}
        </div>
        <p className="text-white/60 text-sm mb-1">{label}</p>
        <p className="text-white text-2xl font-bold">{value}</p>
      </div>
    </motion.div>
  );

  // Notification Item Component
  const NotificationItem = ({ notification }) => {
    const date = new Date(notification.created.seconds * 1000);
    const formatDate = date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });

    let icon = Bell;
    let iconColor = "text-blue-400";

    // More specific icon selection based on notification type
    if (
      notification.type === "emotional_increase" ||
      notification.type === "emotional_decrease"
    ) {
      icon = Heart;
      iconColor = notification.type.includes("increase")
        ? "text-pink-400"
        : "text-pink-600";
    } else if (
      notification.type === "financial_increase" ||
      notification.type === "financial_decrease"
    ) {
      icon = TrendingUp;
      iconColor = notification.type.includes("increase")
        ? "text-green-400"
        : "text-red-400";
    } else if (notification.type === "connection_added") {
      icon = Link2;
      iconColor = "text-purple-400";
    } else if (notification.type === "connection_removed") {
      icon = XCircle;
      iconColor = "text-orange-400";
    } else if (notification.type === "account_closed") {
      icon = CheckCircle;
      iconColor = "text-emerald-400";
    }

    const Icon = icon;

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="flex items-start gap-3 p-2.5 rounded-lg hover:bg-white/[0.02] transition-colors duration-75 cursor-pointer"
      >
        <div className={`p-1.5 rounded-lg bg-white/5 ${iconColor}`}>
          <Icon className="w-3.5 h-3.5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white/70 text-sm line-clamp-2">
            {getNotificationText(notification)}
          </p>
          <div className="flex items-center gap-1.5 mt-0.5">
            <p className="text-white/30 text-xs">{formatDate}</p>
            {notification.sender && (
              <>
                <span className="text-white/20 text-xs">•</span>
                <p className="text-white/30 text-xs">
                  from {notification.sender}
                </p>
              </>
            )}
          </div>
        </div>
      </motion.div>
    );
  };

  const getNotificationText = (not) => {
    switch (not.type) {
      case "emotional_increase": {
        let priorScore = "";
        let newScore = "";
        not.params?.forEach((param) => {
          if (param.name === "prior_score") priorScore = param.value;
          if (param.name === "new_score") newScore = param.value;
        });
        return `Your sentiment score increased from ${priorScore} to ${newScore}`;
      }
      case "emotional_decrease": {
        let priorScore = "";
        let newScore = "";
        not.params?.forEach((param) => {
          if (param.name === "prior_score") priorScore = param.value;
          if (param.name === "new_score") newScore = param.value;
        });
        return `Your sentiment score decreased from ${priorScore} to ${newScore}`;
      }
      case "financial_increase": {
        let priorScore = "";
        let newScore = "";
        not.params?.forEach((param) => {
          if (param.name === "prior_score") priorScore = param.value;
          if (param.name === "new_score") newScore = param.value;
        });
        return `Your financial score increased from ${priorScore} to ${newScore}`;
      }
      case "financial_decrease": {
        let priorScore = "";
        let newScore = "";
        not.params?.forEach((param) => {
          if (param.name === "prior_score") priorScore = param.value;
          if (param.name === "new_score") newScore = param.value;
        });
        return `Your financial score decreased from ${priorScore} to ${newScore}`;
      }
      case "account_closed": {
        let acc_num = "";
        let creditor = "";
        let status = "";
        not.params?.forEach((param) => {
          if (param.name === "acc_num") acc_num = param.value;
          if (param.name === "creditor") creditor = param.value;
          if (param.name === "status") status = param.value;
        });
        return `Account ending in ${acc_num} from ${creditor} has been closed in ${status}`;
      }
      case "connection_added": {
        let type = "";
        not.params?.forEach((param) => {
          if (param.name === "type") type = param.value;
        });
        return `Successfully connected your ${type.replace("twitter", "X")} account`;
      }
      case "connection_removed": {
        let type = "";
        not.params?.forEach((param) => {
          if (param.name === "type") type = param.value;
        });
        return `Removed your ${type.replace("twitter", "X")} account connection`;
      }
      default:
        return not.message || "New notification";
    }
  };

  // Quick Action Component
  const QuickAction = ({ icon: Icon, label, description, onClick, color }) => (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className="relative group text-left w-full"
    >
      <div
        className={`absolute inset-0 bg-gradient-to-r ${color} opacity-0 group-hover:opacity-10 transition-opacity duration-300 rounded-2xl blur-xl`}
      />
      <div className="relative bg-white/[0.03] backdrop-blur-md border border-white/10 rounded-2xl p-4 hover:bg-white/[0.05] transition-all duration-300">
        <div className="flex items-center gap-4">
          <div
            className={`p-3 rounded-xl bg-gradient-to-br ${color.replace("from-", "from-").replace("to-", "/20 to-").replace("to-", "/20")}`}
          >
            <Icon className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <p className="text-white font-medium">{label}</p>
            <p className="text-white/60 text-sm">{description}</p>
          </div>
          <ChevronRight className="w-5 h-5 text-white/40 group-hover:translate-x-1 transition-transform" />
        </div>
      </div>
    </motion.button>
  );

  const hasScores = scoreData?.series?.some((s) => s.data.length > 1);

  return (
    <div className="p-4 lg:p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-7xl mx-auto"
      >
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="flex items-center gap-3 mb-2">
            <motion.div
              initial={{ rotate: -180, scale: 0 }}
              animate={{ rotate: 0, scale: 1 }}
              transition={{ type: "spring", stiffness: 200, delay: 0.3 }}
            >
              <Sparkles className="w-8 h-8 text-yellow-400" />
            </motion.div>
            <h1 className="text-3xl font-bold text-white">
              Welcome back, {mainStore.profile.first_name}!
            </h1>
          </div>
          <p className="text-white/60">
            Here's your financial wellness overview
          </p>
        </motion.div>

        {/* Quick Actions - Primary CTAs - Only show when connected to agency */}
        {mainStore.agency && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-6"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <QuickAction
                icon={CreditCard}
                label="View Accounts"
                description="Review and manage your accounts"
                onClick={() => history.push("/dashboard/accounts")}
                color="from-blue-500 to-cyan-500"
              />
              <QuickAction
                icon={DollarSign}
                label="Make a Payment"
                description="Pay down your balances"
                onClick={() => openModal(modalTypes.ModalMakePayment)}
                color="from-green-500 to-emerald-500"
              />
            </div>
          </motion.div>
        )}

        {/* Stats Grid */}
        {/* <motion.div
          variants={containerAnimation}
          initial="hidden"
          animate="show"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6"
        >
          <StatCard
            icon={Heart}
            label="Sentiment Score"
            value={mainStore.profile.emotional_score || "—"}
            change={mainStore.profile.emotional_change}
            color="from-purple-500 to-pink-500"
            bgColor="from-purple-500/20 to-pink-500/20"
          />
          <StatCard
            icon={TrendingUp}
            label="Financial Score"
            value={mainStore.profile.financial_score || "—"}
            change={mainStore.profile.financial_change}
            color="from-blue-500 to-cyan-500"
            bgColor="from-blue-500/20 to-cyan-500/20"
          />
          <StatCard
            icon={Zap}
            label="Freede Score"
            value={mainStore.profile.freede_score || "—"}
            change={mainStore.profile.freede_change}
            color="from-green-500 to-emerald-500"
            bgColor="from-green-500/20 to-emerald-500/20"
          />
          <StatCard
            icon={Shield}
            label="Account Status"
            value={financialConnected ? "Connected" : "Not Connected"}
            color="from-orange-500 to-red-500"
            bgColor="from-orange-500/20 to-red-500/20"
          />
        </motion.div> */}

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Spending Categories */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="bg-white/[0.03] backdrop-blur-md border border-white/10 rounded-3xl p-6 h-full">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                  <ShoppingCart className="w-5 h-5 text-white/60" />
                  Top Spending Categories
                </h2>
              </div>

              {mainStore.profile.top_spending &&
              mainStore.profile.top_spending.length > 0 ? (
                <div className="space-y-3">
                  {mainStore.profile.top_spending
                    .slice(0, 5)
                    .map((category, index) => {
                      const colors = [
                        "bg-blue-500",
                        "bg-purple-500",
                        "bg-emerald-500",
                        "bg-orange-500",
                        "bg-pink-500",
                      ];
                      const bgColors = [
                        "bg-blue-500/10",
                        "bg-purple-500/10",
                        "bg-emerald-500/10",
                        "bg-orange-500/10",
                        "bg-pink-500/10",
                      ];

                      return (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.1 * index }}
                          className="group"
                        >
                          <div className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.02] hover:bg-white/[0.04] transition-all">
                            <div
                              className={`w-2 h-8 rounded-full ${colors[index % colors.length]}`}
                            />
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-white/80 text-sm font-medium">
                                  {category.category}
                                </span>
                                <span className="text-white/60 text-sm font-medium">
                                  ${category.amount.toFixed(0)}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <div className="flex-1 h-1 bg-white/5 rounded-full overflow-hidden">
                                  <motion.div
                                    initial={{ width: 0 }}
                                    animate={{
                                      width: `${Math.min(category.percent, 100)}%`,
                                    }}
                                    transition={{
                                      duration: 1,
                                      ease: "easeOut",
                                      delay: 0.2 + 0.1 * index,
                                    }}
                                    className={`h-full ${colors[index % colors.length]} opacity-60`}
                                  />
                                </div>
                                <span className="text-white/40 text-xs min-w-[32px] text-right">
                                  {category.percent.toFixed(0)}%
                                </span>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                </div>
              ) : (
                <div className="relative opacity-50">
                  <div className="space-y-3">
                    {[
                      { name: "Housing", percent: 35 },
                      { name: "Food & Dining", percent: 25 },
                      { name: "Transportation", percent: 20 },
                      { name: "Shopping", percent: 12 },
                      { name: "Entertainment", percent: 8 },
                    ].map((item, index) => (
                      <div key={index} className="opacity-[0.07]">
                        <div className="flex items-center gap-3 p-3">
                          <div className="w-2 h-8 rounded-full bg-white" />
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-white text-sm">
                                {item.name}
                              </span>
                              <span className="text-white text-sm">—</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="flex-1 h-1 bg-white/10 rounded-full">
                                <div
                                  className="h-full bg-white rounded-full"
                                  style={{ width: `${item.percent}%` }}
                                />
                              </div>
                              <span className="text-white text-xs min-w-[32px] text-right">
                                {item.percent}%
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="absolute inset-0 flex flex-col items-center justify-center rounded-2xl">
                    <p className="text-white/50 text-sm">
                      No spending data available yet
                    </p>
                  </div>
                </div>
              )}
            </div>
          </motion.div>

          {/* Recent Activity */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div className="bg-white/[0.03] backdrop-blur-md border border-white/10 rounded-3xl p-6 h-full">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-6">
                <Bell className="w-5 h-5 text-white/60" />
                Recent Activity
              </h2>

              <div className="space-y-1 max-h-[280px] overflow-y-auto overflow-x-hidden custom-scrollbar">
                {notifications.length > 0 ? (
                  notifications.map((notification, index) => (
                    <NotificationItem key={index} notification={notification} />
                  ))
                ) : (
                  <div className="flex flex-col items-center justify-center py-8">
                    <motion.div
                      animate={{ rotate: [0, 10, -10, 0] }}
                      transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
                      className="mb-4"
                    >
                      <Bell className="w-12 h-12 text-white/20" />
                    </motion.div>
                    <p className="text-white/40 text-sm">No recent activity</p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.2);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.3);
        }
      `}</style>
    </div>
  );
};

export default Dashboard;
